import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { buscarListasAutoDialer, buscarSessaoAutoDialer, iniciarAutoDialer, retomarListaAutoDialer, type EstadoAutoDialer, type ListaAutoDialer } from '@/lib/autoDialerApi';

export const CHAVE_AUTO_DIALER = ['auto-dialer', 'sessao'] as const;
export const CHAVE_LISTAS_AUTO_DIALER = ['auto-dialer', 'listas'] as const;

/** A sessão do Auto Dialer do consultor. Com sessão aberta, atualiza a cada 1,5 s. */
export function useAutoDialerSessao(ativo = true) {
  return useQuery({
    queryKey: CHAVE_AUTO_DIALER,
    enabled: ativo,
    queryFn: (): Promise<EstadoAutoDialer | null> => buscarSessaoAutoDialer(),
    refetchInterval: (q) => {
      const s = (q.state.data as EstadoAutoDialer | null | undefined)?.sessao?.status;
      return s && s !== 'encerrada' ? 1500 : 20_000;
    },
    refetchIntervalInBackground: true,
    staleTime: 1000,
  });
}

/** Começa a discagem: cria a lista (com nome) e liga na ordem. */
export function useIniciarAutoDialer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: string[] | { leadIds: string[]; nome?: string | null }) =>
      Array.isArray(v) ? iniciarAutoDialer(v) : iniciarAutoDialer(v.leadIds, v.nome),
    onSuccess: (r) => {
      toast.success(`Auto Dialer começou · ${r.nome}`, { description: `${r.leads.toLocaleString('pt-BR')} ${r.leads === 1 ? 'lead' : 'leads'} na fila. Atenda a sala no softphone.` });
      qc.invalidateQueries({ queryKey: CHAVE_AUTO_DIALER });
      qc.invalidateQueries({ queryKey: CHAVE_LISTAS_AUTO_DIALER });
    },
    onError: (e: Error) => toast.error('O Auto Dialer não começou', { description: e.message }),
  });
}

/** Minhas listas do Auto Dialer (30 dias): o que falta e quando volta a próxima rodada. */
export function useListasAutoDialer(ativo = true) {
  return useQuery({
    queryKey: CHAVE_LISTAS_AUTO_DIALER,
    enabled: ativo,
    queryFn: (): Promise<ListaAutoDialer[]> => buscarListasAutoDialer(),
    staleTime: 15_000,
  });
}

export function useRetomarLista() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (listaId: string) => retomarListaAutoDialer(listaId),
    onSuccess: (r) => {
      toast.success(`Retomando · ${r.nome}`, { description: `${r.leads.toLocaleString('pt-BR')} ${r.leads === 1 ? 'lead' : 'leads'} na fila. Atenda a sala no softphone.` });
      qc.invalidateQueries({ queryKey: CHAVE_AUTO_DIALER });
      qc.invalidateQueries({ queryKey: CHAVE_LISTAS_AUTO_DIALER });
    },
    onError: (e: Error) => toast.error('Não retomou', { description: e.message }),
  });
}
